import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import type {
  Conversation,
  ConversationMessage,
  MessagingPlatform,
} from '@/features/conversations/api';
import { cn } from '@/lib/utils';

import { useConversationCalls } from '@/features/voice-receptionist/api';
import { AdReferralCard } from './ad-referral-card';
import { CallCard } from './call-card';
import { ConversationMobileMessageBubble } from './conversation-mobile-message-bubble';
import {
  anchorCalls,
  callSidOf,
  callTranscriptPreviews,
  conversationHandoff,
  conversationLeadId,
  isLeadFormSubmission,
  lastAgentReplyAt,
  openTeamFollowUps,
} from './conversation-thread-events';
import { DaySeparatorBadge, dayKey, formatDayLabel } from './day-separator';
import { HandoffCard } from './handoff-card';
import { LeadFormCard } from './lead-form-card';
import { findLastOutboundIndex } from './message-delivery-status';
import { ChannelSwitch } from './reply-channel';
import { TeamFollowUpCard } from './team-follow-up-card';

interface ConversationMobileMessageThreadProps {
  conversation: Conversation;
  messages: ConversationMessage[];
  memberFirstNameById: Map<string, string>;
  memberFullNameById: Map<string, string>;
  memberImageById: Map<string, string | null>;
  className?: string;
  composerLayoutVersion?: number;
}

export function ConversationMobileMessageThread({
  conversation,
  messages,
  memberFirstNameById,
  memberFullNameById,
  memberImageById,
  className,
  composerLayoutVersion = 0,
}: ConversationMobileMessageThreadProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const lastOutboundIndex = findLastOutboundIndex(messages);
  const leadId = conversationLeadId(conversation);
  const handoff = conversationHandoff(conversation);
  // Merged inbox: timeline messages carry their channel; mark where it changes.
  const platformOf = (m: ConversationMessage) =>
    (m as ConversationMessage & { platform?: MessagingPlatform }).platform;
  const { calls } = useConversationCalls(
    conversation.id,
    conversation.platform === 'phone' ||
      Boolean(conversationLeadId(conversation)) ||
      messages.some((m) => platformOf(m) === 'phone')
  );
  const { byMessageId: callAbove, trailing: trailingCalls } = anchorCalls(
    messages,
    calls
  );
  const transcriptPreviews = callTranscriptPreviews(messages);
  const callIds = new Set(calls.map((call) => call.id));
  const multiChannel =
    new Set(messages.map(platformOf).filter(Boolean)).size > 1;
  const lastSystemIndex = messages.reduce(
    (found, m, i) => (m.role === 'system' && !callSidOf(m) ? i : found),
    -1
  );

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll when message list or composer height changes
  useEffect(() => {
    scrollToBottom();
  }, [messages.length, composerLayoutVersion, scrollToBottom]);

  return (
    <div
      className={cn(
        'min-h-0 flex-1 overflow-y-auto [-webkit-overflow-scrolling:touch]',
        className
      )}
    >
      <div className="flex flex-col gap-2.5 px-4 py-3">
        <AdReferralCard metadata={conversation.metadata} />
        {leadId ? <LeadFormCard leadId={leadId} /> : null}
        {handoff && lastSystemIndex === -1 ? (
          <HandoffCard
            reason={handoff.reason}
            detail={handoff.detail}
            at={handoff.at}
          />
        ) : null}
        {messages.map((message, index) => {
          if (isLeadFormSubmission(message)) return null;
          const isLastOutbound = index === lastOutboundIndex;
          const iso = message.sentAt ?? message.createdAt;
          const prev = index > 0 ? messages[index - 1] : null;
          const prevIso = prev ? (prev.sentAt ?? prev.createdAt) : null;
          const showDayBadge = !prevIso || dayKey(prevIso) !== dayKey(iso);
          const callsHere = callAbove.get(message.id) ?? [];
          const transcriptInCard = callIds.has(callSidOf(message) ?? '');
          if (transcriptInCard) return null;
          const channel = platformOf(message);
          const channelChanges =
            multiChannel &&
            channel !== undefined &&
            (!prev || platformOf(prev) !== channel);
          return (
            <Fragment key={message.id}>
              {showDayBadge ? (
                <DaySeparatorBadge label={formatDayLabel(new Date(iso))} />
              ) : null}
              {channelChanges && channel ? (
                <ChannelSwitch
                  platform={channel}
                  transport={message.metadata?.transport}
                />
              ) : null}
              {callsHere.map((call) => (
                <CallCard
                  key={call.id}
                  call={call}
                  previewLines={transcriptPreviews.get(call.id)}
                />
              ))}
              {message.role === 'system' &&
              index === lastSystemIndex &&
              handoff ? (
                <HandoffCard
                  reason={handoff.reason}
                  detail={handoff.detail}
                  at={handoff.at ?? iso}
                />
              ) : (
                <ConversationMobileMessageBubble
                  message={message}
                  conversation={conversation}
                  memberFirstNameById={memberFirstNameById}
                  memberFullNameById={memberFullNameById}
                  memberImageById={memberImageById}
                  showDelivered={isLastOutbound}
                />
              )}
            </Fragment>
          );
        })}
        {trailingCalls.map((call) => (
          <CallCard
            key={call.id}
            call={call}
            previewLines={transcriptPreviews.get(call.id)}
          />
        ))}
        <TeamFollowUpCard
          requests={openTeamFollowUps(conversation, lastAgentReplyAt(messages))}
        />
        <div ref={bottomRef} aria-hidden />
      </div>
    </div>
  );
}

/** Hook for composer resize → thread scroll (Ask AI sheet parity). */
export function useConversationMobileComposerLayout() {
  const [composerLayoutVersion, setComposerLayoutVersion] = useState(0);
  const onComposerLayoutChange = useCallback(() => {
    setComposerLayoutVersion((v) => v + 1);
  }, []);
  return { composerLayoutVersion, onComposerLayoutChange };
}
